/**
 * Notices when the tab is running a bundle older than what's deployed.
 *
 * A content-hashed bundle is cached forever by design, so a tab left open —
 * which is exactly what happens with a site you check every few days — will
 * happily run last week's code until something forces a reload. Rather than
 * guess at cache headers, the app just asks: is my build id still the current
 * one? Checked on focus, because that's when someone is about to use it.
 */
import { useEffect, useState } from 'react'

declare const __BUILD_ID__: string

const CHECK_EVERY_MS = 10 * 60 * 1000

export function useFreshness(): { stale: boolean; reload: () => void } {
  const [stale, setStale] = useState(false)

  useEffect(() => {
    let cancelled = false
    let lastCheck = 0

    async function check() {
      if (stale || cancelled) return
      if (Date.now() - lastCheck < 30_000) return // don't hammer on tab flicking
      lastCheck = Date.now()
      try {
        const res = await fetch('/version.json', { cache: 'no-store' })
        if (!res.ok) return
        const { id } = (await res.json()) as { id: string }
        if (!cancelled && id && id !== __BUILD_ID__) setStale(true)
      } catch {
        // Offline, or the check failed. Not worth mentioning.
      }
    }

    const onFocus = () => { if (document.visibilityState === 'visible') void check() }
    document.addEventListener('visibilitychange', onFocus)
    window.addEventListener('focus', onFocus)
    const timer = window.setInterval(() => void check(), CHECK_EVERY_MS)
    void check()

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onFocus)
      window.removeEventListener('focus', onFocus)
      window.clearInterval(timer)
    }
  }, [stale])

  return { stale, reload: () => window.location.reload() }
}
